import { ReactNode, useCallback, useEffect, useRef, useState } from "react"
import {
  getPinnedCloseAttemptHandler,
  setCloseAttemptHandler,
  setPinnedCloseAttemptHandler,
} from "@/stores/use-global-dialog-store"

import { ConfirmDialog } from "@/components/dialogs"

import { useDialogCloseRegistry } from "./dialog-close-context"
import { useDialogEntryId } from "./dialog-entry-context"

interface UseDialogCloseGuardArgs {
  /** Whether the dialog has unsaved changes. When false, closing happens immediately. */
  isDirty: boolean
  /** Lowercase entity name for the prompt, e.g. "note", "task". */
  entityLabel: string
  /** Tears the dialog down for real (clear draft, close) once a close is confirmed. */
  onClose: () => void
  /**
   * When this dialog is pinned it stays mounted next to the active global dialog, so its guard is
   * registered in the keyed pinned registry (by dialogId) instead of the shared global handler —
   * PinnedDialog routes the pinned close through it. Requires dialogId. Defaults to the global handler.
   */
  isPinned?: boolean
  /** Stable id of this dialog's draft, used to key the pinned registry when isPinned. */
  dialogId?: string
}

interface UseDialogCloseGuardResult {
  /** Wire this to Cancel buttons. The X and Escape route here automatically via GlobalDialog. */
  requestClose: () => void
  /** Guards another action that discards the current draft, such as navigating away. */
  requestDiscard: (onDiscard: () => void) => void
  /** Render this inside the dialog — the "Discard unsaved ..." confirmation. */
  discardConfirm: ReactNode
}

/**
 * Standardizes close behavior for dialogs rendered through GlobalDialog:
 *  - Registers a close-attempt handler so the X and Escape route through here.
 *  - If there are unsaved changes, shows a "Discard unsaved {entity}?" confirm
 *    instead of closing; otherwise closes immediately.
 *
 * Pairs with GlobalDialog's onInteractOutside, which never lets an outside click
 * dismiss the dialog directly and instead routes it here, so a click outside gets
 * the same "Discard?" treatment as the X, a Cancel button, or Escape.
 */
export function useDialogCloseGuard({
  isDirty,
  entityLabel,
  onClose,
  isPinned = false,
  dialogId,
}: UseDialogCloseGuardArgs): UseDialogCloseGuardResult {
  const entryId = useDialogEntryId()
  const localCloseRegistry = useDialogCloseRegistry()
  const [showConfirm, setShowConfirm] = useState(false)
  const pendingDiscardRef = useRef<(() => void) | null>(null)

  const requestDiscard = useCallback(
    (onDiscard: () => void) => {
      if (isDirty) {
        pendingDiscardRef.current = onDiscard
        setShowConfirm(true)
      } else {
        onDiscard()
      }
    },
    [isDirty]
  )

  const requestClose = useCallback(() => {
    requestDiscard(onClose)
  }, [onClose, requestDiscard])

  useEffect(() => {
    // A pinned dialog keys its guard by dialogId so PinnedDialog can route that pinned dialog's
    // close through it — and so it never overwrites the active global dialog's shared handler.
    if (isPinned && dialogId) {
      setPinnedCloseAttemptHandler(dialogId, requestClose)
      return () => {
        if (getPinnedCloseAttemptHandler(dialogId) === requestClose) setPinnedCloseAttemptHandler(dialogId, null)
      }
    }

    if (!entryId) return localCloseRegistry?.register(requestClose)

    setCloseAttemptHandler(requestClose, entryId)
    return () => setCloseAttemptHandler(null, entryId)
  }, [requestClose, isPinned, dialogId, entryId, localCloseRegistry])

  const discardConfirm = (
    <ConfirmDialog
      open={showConfirm}
      onOpenChange={setShowConfirm}
      title={`Discard unsaved ${entityLabel}?`}
      description={"Any unsaved changes will be lost. Are you sure you would like to close this draft?"}
      dismiss={"explicit"}
      cancel={{
        onClick: () => {
          pendingDiscardRef.current = null
          setShowConfirm(false)
        },
      }}
      action={{
        label: "Discard",
        onClick: () => {
          const onDiscard = pendingDiscardRef.current ?? onClose
          pendingDiscardRef.current = null
          setShowConfirm(false)
          onDiscard()
        },
      }}
    />
  )

  return { requestClose, requestDiscard, discardConfirm }
}
